/**
 * Narrow types for the Maps JavaScript **Places API (New)** autocomplete widget.
 *
 * @see https://developers.google.com/maps/documentation/javascript/places-migration-autocomplete
 */

/** Payload for the `gmp-select` event on {@link google.maps.places.PlaceAutocompleteElement}. */
export interface GmpSelectAutocompleteEvent extends Event {
  readonly placePrediction: PlacePredictionLike;
}

export interface PlacePredictionLike {
  /** Place resource id when provided by the widget (avoids Details fetch for cache lookup). */
  placeId?: string;
  /** @returns A {@link PlaceLike} instance to call {@link PlaceLike.fetchFields} on. */
  toPlace: () => PlaceLike;
}

export interface PlaceLike {
  id?: string;
  displayName?: string | { text: string };
  formattedAddress?: string;
  location?: google.maps.LatLng | null;
  types?: string[];
  /** Place Details Enterprise — public website (`websiteURI` in fetchFields). */
  websiteURI?: string;
  /** Place Details Enterprise — maps to `cbo_record.phone_number` via form `phoneNumber`. */
  nationalPhoneNumber?: string;
  /**
   * Fetches the requested fields into this instance (mutating).
   *
   * @param opts - Field list (camelCase). Phone and website require **Place Details Enterprise**; see
   *   https://developers.google.com/maps/documentation/places/web-service/data-fields
   */
  fetchFields(opts: { fields: string[] }): Promise<void>;
}

export interface PlaceAutocompleteElementConstructor {
  new (options?: {
    includedPrimaryTypes?: string[];
    placeholder?: string;
  }): HTMLElement & {
    addEventListener(
      type: 'gmp-select',
      listener: (this: HTMLElement, ev: Event) => void | Promise<void>
    ): void;
    removeEventListener(
      type: 'gmp-select',
      listener: (this: HTMLElement, ev: Event) => void | Promise<void>
    ): void;
  };
}

/**
 * @returns The Places (New) autocomplete constructor, or undefined if not available yet.
 */
export function getPlaceAutocompleteElementConstructor():
  | PlaceAutocompleteElementConstructor
  | undefined {
  const places = google.maps.places as unknown as {
    PlaceAutocompleteElement?: PlaceAutocompleteElementConstructor;
  };
  return places.PlaceAutocompleteElement;
}
